import * as THREE from 'three';
import type { Item, Level, Vec2 } from '../../core/hp/types';
import { isPlanItemVisible, type PlanSurface } from '../../state/planVisibility';
import { cloneFullModel, fullGeometryOf } from './modeVisibility';
import { meshSurfaceProbe } from './surfaceProbe';
import type { SurfaceProbe } from '../../core/geometry/stacking';

export interface PlanFurnitureRequest {
  level: Level;
  viewport: { centre: Vec2; scale: number };
  width: number;
  height: number;
  surface: PlanSurface;
  dpr?: number;
}
interface RetainedCopy { source: THREE.Object3D; copy: THREE.Object3D; stamp: string; release: () => void }
interface PlanSourceOptions {
  node?(name: string): THREE.Object3D | undefined;
  localBounds?(name: string): THREE.Box3 | undefined;
  retainResources?(root: THREE.Object3D): () => void;
  modelTags?(id: string): readonly string[] | undefined;
}
export interface PlanFootprint { minX: number; maxX: number; minZ: number; maxZ: number }
export interface PlanFurnitureLayer {
  render(request: PlanFurnitureRequest): HTMLCanvasElement | null;
  subscribe(listener: () => void): () => void;
  footprint(item: Item): PlanFootprint | undefined;
  /** The height of the highest loaded surface of `item` under any of `points`, or null when none is over its meshes. */
  surfaceHeight?: SurfaceProbe;
  /** The catalog tags of a model the scene has loaded: they pick a door's or a window's plan symbol. */
  modelTags?(id: string): readonly string[] | undefined;
}

/** Same assembled meshes, one depth buffer. Rendering is invalidation-driven, never RAF. */
export class PlanTopView implements PlanFurnitureLayer {
  private readonly canvas = document.createElement('canvas');
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.OrthographicCamera();
  private content = new THREE.Group();
  private version = 0;
  private key = '';
  private lastItems: readonly Item[] | null = null;
  private synced: { version: number; items: readonly Item[]; surface: PlanSurface; height: number; rooms: Level['rooms'] } | null = null;
  private copies = new Map<string, RetainedCopy>();
  private dirty = new Set<string>();
  private allDirty = true;
  cloneCount = 0;
  private listeners = new Set<() => void>();
  private footprints = new WeakMap<THREE.Object3D, { scale: string; bounds: PlanFootprint }>();
  renderFrames = 0;

  constructor(private renderer: THREE.WebGLRenderer, private source: () => THREE.Group, environment: THREE.Texture | null, private options: PlanSourceOptions = {}) {
    this.scene.environment = environment;
    this.scene.environmentIntensity = .5;
    this.scene.add(new THREE.HemisphereLight(0xffffff, 0x888888, .7));
    const sun = new THREE.DirectionalLight(0xffffff, 2.4);
    sun.position.set(-2, 12, -3);
    this.scene.add(sun, this.content);
  }

  invalidate(name?: string): void {
    if (name) this.dirty.add(name); else this.allDirty = true;
    this.version++;
    for (const listener of this.listeners) listener();
  }
  subscribe(listener: () => void): () => void { this.listeners.add(listener); return () => this.listeners.delete(listener); }

  readonly surfaceHeight: SurfaceProbe = meshSurfaceProbe(name => this.options.node?.(name) ?? this.source().getObjectByName(name));

  modelTags(id: string): readonly string[] | undefined { return this.options.modelTags?.(id); }

  footprint(item: Item): PlanFootprint | undefined {
    const name = `item:${item.id}`;
    const node = this.options.node?.(name) ?? this.source().getObjectByName(name);
    if (!node || (!node.userData.real && !node.userData.generated)) return undefined;
    const cached = this.footprints.get(node);
    const scale = node.scale.toArray().join(',');
    if (cached?.scale === scale) return cached.bounds;
    let bounds = this.options.localBounds?.(name);
    if (!bounds) {
      const copy = cloneFullModel(node); copy.position.set(0, 0, 0); copy.rotation.set(0, 0, 0);
      bounds = new THREE.Box3().setFromObject(copy);
    }
    if (bounds.isEmpty()) return undefined;
    const footprint = { minX: bounds.min.x, maxX: bounds.max.x, minZ: bounds.min.z, maxZ: bounds.max.z };
    this.footprints.set(node, { scale, bounds: footprint });
    return footprint;
  }

  render(request: PlanFurnitureRequest): HTMLCanvasElement | null {
    const { level, viewport, width, height, surface } = request;
    if (!(width > 0 && height > 0 && viewport.scale > 0)) return null;
    const dpr = Math.min(request.dpr ?? window.devicePixelRatio ?? 1, 2);
    const key = JSON.stringify([this.version, viewport, width, height, surface, dpr]);
    if (this.key === key && this.lastItems === level.items && this.synced?.rooms === level.rooms && this.synced?.height === level.height) return this.canvas;
    let contentChanged = false;
    // Panning/zooming changes the camera, not the assemblies. Hydration, transforms and
    // textures invalidate this independently, so remote edits still appear on the next draw.
    if (!this.synced || this.synced.version !== this.version || this.synced.items !== level.items
      || this.synced.surface !== surface || this.synced.height !== level.height || this.synced.rooms !== level.rooms) {
      const wanted = new Set<string>();
      for (const item of level.items) {
        if (!isPlanItemVisible(item, surface, level.height)) continue;
        const name = `item:${item.id}`, node = this.options.node?.(name) ?? this.source().getObjectByName(name);
        if (!node) continue;
        wanted.add(name);
        let retained = this.copies.get(name);
        if (!retained || retained.source !== node || this.allDirty || this.dirty.has(name)) {
          const stamp = resourceStamp(node);
          if (!retained || retained.source !== node || retained.stamp !== stamp) {
            const copy = cloneFullModel(node), release = this.options.retainResources?.(copy) ?? (() => {});
            if (retained) { retained.release(); retained.copy.removeFromParent(); }
            retained = { source: node, copy, stamp, release }; this.copies.set(name, retained);
            this.content.add(copy); this.cloneCount++; contentChanged = true;
          }
        }
        const copy = retained.copy;
        const position = node.position.clone(), scale = node.scale.clone();
        if (surface === 'ceiling' && item.placement === 'ceiling') {
          scale.y *= -1; position.y += 2 * item.size.h;
        }
        contentChanged ||= !copy.position.equals(position) || !copy.scale.equals(scale) || !copy.quaternion.equals(node.quaternion);
        copy.position.copy(position); copy.quaternion.copy(node.quaternion); copy.scale.copy(scale); copy.visible = true;
      }
      for (const [name, retained] of this.copies) if (!wanted.has(name)) {
        retained.release(); retained.copy.removeFromParent(); this.copies.delete(name); contentChanged = true;
      }
      this.dirty.clear(); this.allDirty = false;
      this.synced = { version: this.version, items: level.items, surface, height: level.height, rooms: level.rooms };
    }
    if (this.key === key && !contentChanged) { this.lastItems = level.items; return this.canvas; }
    configurePlanCamera(this.camera, request);
    const renderer = this.renderer;
    const size = renderer.getSize(new THREE.Vector2());
    const pixelRatio = renderer.getPixelRatio();
    const clearColor = renderer.getClearColor(new THREE.Color());
    const clearAlpha = renderer.getClearAlpha();
    const target = renderer.getRenderTarget();
    const exposure = renderer.toneMappingExposure;
    const shadows = renderer.shadowMap.enabled;
    const viewportBefore = renderer.getViewport(new THREE.Vector4());
    const scissor = renderer.getScissor(new THREE.Vector4());
    const scissorTest = renderer.getScissorTest();
    try {
      renderer.setRenderTarget(null);
      if (pixelRatio !== dpr) renderer.setPixelRatio(dpr);
      if (size.x !== width || size.y !== height) renderer.setSize(width, height, false);
      renderer.setScissorTest(false);
      renderer.setClearColor(0xffffff, 0);
      renderer.toneMappingExposure = 1;
      renderer.shadowMap.enabled = false;
      renderer.render(this.scene, this.camera);
      const pixelWidth = Math.round(width * dpr), pixelHeight = Math.round(height * dpr);
      if (this.canvas.width !== pixelWidth || this.canvas.height !== pixelHeight) { this.canvas.width = pixelWidth; this.canvas.height = pixelHeight; }
      const ctx = this.canvas.getContext('2d')!;
      ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
      ctx.drawImage(renderer.domElement, 0, 0, this.canvas.width, this.canvas.height);
      this.renderFrames++;
      this.key = key; this.lastItems = level.items;
    } finally {
      renderer.shadowMap.enabled = shadows;
      renderer.toneMappingExposure = exposure;
      renderer.setClearColor(clearColor, clearAlpha);
      if (pixelRatio !== dpr) renderer.setPixelRatio(pixelRatio);
      if (size.x !== width || size.y !== height) renderer.setSize(size.x, size.y, false);
      renderer.setRenderTarget(target);
      renderer.setViewport(viewportBefore); renderer.setScissor(scissor); renderer.setScissorTest(scissorTest);
    }
    return this.canvas;
  }

  clear(): void {
    for (const retained of this.copies.values()) { retained.release(); retained.copy.removeFromParent(); }
    this.copies.clear(); this.synced = null; this.lastItems = null; this.key = ''; this.allDirty = true; this.dirty.clear();
  }
  dispose(): void { this.listeners.clear(); this.clear(); this.canvas.width = this.canvas.height = 1; }
}

export function configurePlanCamera(camera: THREE.OrthographicCamera, request: PlanFurnitureRequest): void {
  const { viewport, width, height } = request;
  const halfW = width * viewport.scale / 2, halfH = height * viewport.scale / 2;
  camera.left = -halfW; camera.right = halfW; camera.top = halfH; camera.bottom = -halfH;
  camera.near = .01; camera.far = 1000;
  camera.position.set(viewport.centre.x, 500, viewport.centre.z);
  camera.up.set(0, 0, -1);
  camera.lookAt(viewport.centre.x, 0, viewport.centre.z);
  camera.updateProjectionMatrix(); camera.updateMatrixWorld();
}

function resourceStamp(root: THREE.Object3D): string {
  const parts: string[] = [];
  root.traverse(node => {
    if (node !== root) parts.push(node.matrix.elements.join(','));
    const mesh = node as THREE.Mesh;
    if (!mesh.isMesh) return;
    parts.push(fullGeometryOf(mesh).uuid);
    for (const material of Array.isArray(mesh.material) ? mesh.material : [mesh.material]) {
      parts.push(material.uuid);
      for (const value of Object.values(material)) if (value instanceof THREE.Texture) parts.push(value.uuid, value.source.uuid);
    }
  });
  return parts.join('|');
}
